/**
 * Enhanced Skeleton Loading Components
 * Simple, static skeleton loaders with pulse animation
 */

import { t } from "@lingui/macro";
import { cn } from "@stylingcv/utils";

// Base Skeleton with simple pulse effect (no slide animation)
export const Skeleton = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      className={cn(
        "animate-pulse rounded-md bg-gray-200 dark:bg-gray-700",
        className,
      )}
      {...props}
    />
  );
};

// Resume Card Skeleton
export const ResumeCardSkeleton = () => {
  return (
    <div className="rounded-lg border bg-card p-6 shadow-sm">
      <div className="space-y-4">
        {/* Title */}
        <Skeleton className="h-6 w-3/4" />

        {/* Metadata */}
        <div className="flex items-center space-x-4">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-24" />
        </div>

        {/* Preview */}
        <Skeleton className="h-32 w-full rounded" />

        {/* Actions */}
        <div className="flex space-x-2">
          <Skeleton className="h-9 w-20" />
          <Skeleton className="h-9 w-20" />
        </div>
      </div>
    </div>
  );
};

// Form Field Skeleton
export const FormFieldSkeleton = () => {
  return (
    <div className="space-y-2">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-10 w-full" />
    </div>
  );
};

// Dashboard Stats Skeleton
export const DashboardStatsSkeleton = () => {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={i} className="rounded-lg border bg-card p-6 shadow-sm">
          <div className="space-y-2">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-8 w-20" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
      ))}
    </div>
  );
};

// Navigation Skeleton
export const NavigationSkeleton = () => {
  return (
    <div className="space-y-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex items-center space-x-3 rounded-lg p-3">
          <Skeleton className="size-5" />
          <Skeleton className="h-4 w-24" />
        </div>
      ))}
    </div>
  );
};

// Table Skeleton
export const TableSkeleton = ({ rows = 5, columns = 4 }: { rows?: number; columns?: number }) => {
  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex space-x-4">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} className="h-4 flex-1" />
        ))}
      </div>

      {/* Rows */}
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex space-x-4">
          {Array.from({ length: columns }).map((_, j) => (
            <Skeleton key={j} className="h-4 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
};

// Text Block Skeleton
export const TextBlockSkeleton = ({ lines = 3 }: { lines?: number }) => {
  return (
    <div className="space-y-2">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={cn(
            "h-4",
            i === lines - 1 ? "w-3/4" : "w-full", // Last line shorter
          )}
        />
      ))}
    </div>
  );
};

// Button Skeleton
export const ButtonSkeleton = ({ size = "default" }: { size?: "sm" | "default" | "lg" }) => {
  const sizeClasses = {
    sm: "h-8 w-16",
    default: "h-10 w-20",
    lg: "h-12 w-24",
  };

  return <Skeleton className={cn("rounded-md", sizeClasses[size])} />;
};

// Avatar Skeleton
export const AvatarSkeleton = ({ size = 40 }: { size?: number }) => {
  return <Skeleton className="rounded-full" style={{ width: size, height: size }} />;
};

// Modern Dashboard Stats Skeleton (3 columns)
export const ModernDashboardStatsSkeleton = () => {
  return (
    <div className="mb-6 overflow-hidden rounded-2xl border border-gray-200 bg-white/80 dark:border-amber-900/30 dark:bg-neutral-900/80">
      <div className="grid grid-cols-1 divide-y divide-gray-100 dark:divide-amber-900/20 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-center justify-between gap-3 p-4">
            <div className="flex items-center gap-3">
              <Skeleton className="size-10 rounded-xl" />
              <div className="space-y-2">
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-5 w-24" />
              </div>
            </div>
            <Skeleton className="h-8 w-20 rounded-md" />
          </div>
        ))}
      </div>
    </div>
  );
};

// Dashboard Page Skeleton
export const DashboardPageSkeleton = () => {
  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6">
      <ModernDashboardStatsSkeleton />

      {/* Resumes Header Skeleton */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <Skeleton className="h-8 w-48" />
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-10 w-24" />
        </div>
      </div>

      {/* Grid Skeleton */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
        {Array.from({ length: 6 }).map((_, i) => (
          <ResumeCardSkeleton key={i} />
        ))}
      </div>
    </div>
  );
};

// Templates Page Skeleton
export const TemplatesPageSkeleton = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section Skeleton */}
      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl text-center space-y-6">
          <Skeleton className="mx-auto h-12 w-64 sm:h-16 sm:w-96" />
          <Skeleton className="mx-auto h-4 w-full max-w-2xl" />
          <Skeleton className="mx-auto h-4 w-3/4 max-w-xl" />
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Skeleton className="h-12 w-40" />
            <Skeleton className="h-12 w-40" />
          </div>
        </div>
      </section>

      {/* Filters Skeleton */}
      <section className="sticky top-0 z-40 border-b bg-background/95 px-4 py-3 backdrop-blur-sm sm:py-4">
        <div className="mx-auto max-w-7xl flex items-center justify-center gap-2 overflow-x-auto sm:gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-28 shrink-0 rounded-md" />
          ))}
        </div>
      </section>

      {/* Grid Skeleton */}
      <section className="px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="rounded-xl border bg-card p-4 shadow-sm space-y-4">
                <div className="space-y-2">
                  <Skeleton className="h-5 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                </div>
                <Skeleton className="aspect-[3/4] w-full rounded-lg" />
                <Skeleton className="h-10 w-full rounded-md" />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// Loading Page Skeleton - Full page loading state
export const LoadingPageSkeleton = () => {
  return (
    <div className="min-h-screen bg-background p-6">
      <div className="mx-auto max-w-6xl space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Skeleton className="h-10 w-48" />
          <div className="flex gap-2">
            <Skeleton className="h-10 w-24" />
            <Skeleton className="h-10 w-24" />
          </div>
        </div>

        {/* Stats */}
        <DashboardStatsSkeleton />

        {/* Content Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <ResumeCardSkeleton key={i} />
          ))}
        </div>
      </div>
    </div>
  );
};
